import { useState, useEffect, useRef } from 'react';
import { Input } from './input';
import { MapPin, Loader2, X, Search } from 'lucide-react';
import { cn } from '../../lib/utils';

// Simple debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export const AddressAutocomplete = ({
  value = '',
  onChange,
  onAddressSelect,
  placeholder = "Escribe la dirección...",
  className,
  disabled = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  
  const debouncedQuery = useDebounce(inputValue, 400);

  // Sync with external value changes
  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  // Fetch suggestions from Nominatim (OpenStreetMap)
  useEffect(() => {
    const fetchSuggestions = async () => {
      // Only search if query is long enough
      if (!debouncedQuery || debouncedQuery.length < 4) {
        setSuggestions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Build search query - add Spain context for better results
        const searchQuery = debouncedQuery.toLowerCase().includes('españa') || debouncedQuery.toLowerCase().includes('spain')
          ? debouncedQuery
          : `${debouncedQuery}, España`;
        
        const params = new URLSearchParams({
          q: searchQuery,
          format: 'json',
          addressdetails: '1',
          limit: '5',
          countrycodes: 'es',
          'accept-language': 'es'
        });
        
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?${params}`,
          {
            headers: {
              'User-Agent': 'AquaNexia/1.0 (contact@aquanexia.com)'
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Error en búsqueda');
        }
        
        const data = await response.json();
        
        // Transform and filter results
        const formattedResults = data
          .filter(item => item.address) // Only items with address details
          .map(item => {
            const addr = item.address || {};
            
            // Build a clean address string
            let street = '';
            if (addr.road) {
              street = addr.road;
              if (addr.house_number) {
                street += ' ' + addr.house_number;
              }
            }
            
            const city = addr.city || addr.town || addr.village || addr.municipality || addr.county || '';
            const postcode = addr.postcode || '';
            const state = addr.state || addr.province || '';
            
            // Create formatted display
            const parts = [street, city, postcode].filter(Boolean);
            const formatted = parts.join(', ') || item.display_name.split(',').slice(0, 3).join(', ');
            
            return {
              display_name: item.display_name,
              formatted: formatted,
              lat: item.lat,
              lon: item.lon,
              city: city,
              postcode: postcode,
              state: state,
              road: addr.road || '',
              house_number: addr.house_number || ''
            };
          });
        
        setSuggestions(formattedResults);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Error buscando direcciones:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle input change - always allow free text
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    // Always call onChange to update parent state
    if (onChange) {
      onChange(newValue);
    }
    setShowSuggestions(true);
  };

  // Handle selecting a suggestion
  const handleSelectSuggestion = (suggestion) => {
    const newValue = suggestion.formatted;
    setInputValue(newValue);
    
    // Update parent with the address string
    if (onChange) {
      onChange(newValue);
    }
    
    // Call the special handler with full address data
    if (onAddressSelect) {
      onAddressSelect({
        address: newValue,
        fullAddress: suggestion.display_name,
        city: suggestion.city || '',
        postal_code: suggestion.postcode || '',
        lat: suggestion.lat,
        lon: suggestion.lon
      });
    }
    
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Clear input
  const clearInput = () => {
    setInputValue('');
    if (onChange) {
      onChange('');
    }
    setSuggestions([]);
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 4 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-9 pr-8"
          autoComplete="off"
          {...props}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
        )}
        {!loading && inputValue && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <div className="p-2 text-xs text-slate-400 border-b flex items-center gap-1">
            <Search className="w-3 h-3" />
            Sugerencias (puedes escribir manualmente)
          </div>
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={cn(
                "w-full px-4 py-3 text-left text-sm hover:bg-slate-50 flex items-start gap-3 border-b last:border-b-0 transition-colors",
                selectedIndex === index && "bg-sky-50"
              )}
            >
              <MapPin className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">
                  {suggestion.formatted}
                </p>
                {suggestion.city && (
                  <p className="text-xs text-slate-500 truncate">
                    {[suggestion.city, suggestion.state, suggestion.postcode]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Help text when typing */}
      {showSuggestions && !loading && inputValue.length >= 4 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-sm text-slate-500">
          <p>No se encontraron sugerencias.</p>
          <p className="text-xs mt-1">Puedes escribir la dirección manualmente.</p>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
